import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { 
  CalendarCheck, 
  CheckCircle,
  Clock,
  User
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Bookings() {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchBookings();
  }, []);
  
  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const statusColors = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="p-8" data-testid="bookings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold text-zinc-900 tracking-tight">
          {t('bookings')}
        </h1>
        <p className="text-zinc-500 mt-1">
          Bookinger oprettet af Polly — nyeste først
        </p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                <CalendarCheck size={24} weight="duotone" className="text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-900">{bookings.length}</p>
                <p className="text-sm text-zinc-500">Total Bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={24} weight="duotone" className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-900">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
                <p className="text-sm text-zinc-500">{t('confirmed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock size={24} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-zinc-900">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
                <p className="text-sm text-zinc-500">{t('pending')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Bookings Table */}
      <Card className="bg-white border-zinc-200">
        <CardHeader>
          <CardTitle className="text-lg">{t('allBookings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-zinc-200">
                  <TableHead className="text-left">Booking ID</TableHead>
                  <TableHead className="text-left">Kunde</TableHead>
                  <TableHead className="text-left">Behandling</TableHead>
                  <TableHead className="text-left">Lokation</TableHead>
                  <TableHead className="text-left">Dato</TableHead>
                  <TableHead className="text-left">{t('status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((booking, idx) => (
                  <TableRow key={booking.id || booking.bookingId || idx} className="border-b border-zinc-100" data-testid={`booking-row-${booking.id || idx}`}>
                    <TableCell className="font-mono text-sm font-medium">
                      {booking.bookingId || booking.confirmation_code || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center">
                          <User size={12} className="text-zinc-500" />
                        </div>
                        <span className="text-sm text-zinc-600">
                          {booking.customer_name || booking.user_id || '-'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-900">{booking.treatment || booking.service_type || '-'}</TableCell>
                    <TableCell className="text-zinc-600">{booking.location || '-'}</TableCell>
                    <TableCell className="text-zinc-600">{booking.date || booking.booking_date || '-'}</TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[booking.status || 'confirmed']} text-xs`}>
                        {booking.status || 'confirmed'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-12 text-center text-zinc-500">
              <CalendarCheck size={48} className="mx-auto mb-3 text-zinc-300" />
              <p>{t('noBookings')}</p>
              <p className="text-sm text-zinc-400 mt-1">
                Bookings will appear here when users schedule appointments via the chatbot
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
